import React from 'react';
import { useInfoCardLogic } from './useInfoCard.logic';
import { getTagText, type Tag } from '../utils/tagUtils';
import 'primeicons/primeicons.css';
import './InfoCard.styles.scss';

export interface InfoCardProps {
  sectionType?:
    | 'contractExpirations'
    | 'andaUpdates'
    | 'lossOfExclusivity'
    | 'preSolicitationAlerts'
    | 'marketIntelligence';
  title: string;
  date: string;
  description: string;
  tags?: Tag[];
  priority?: 'high' | 'medium' | 'normal';
  url?: string;
  onCardClick?: () => void;
  cardColor?: string; // Dynamic accent color for card border (e.g., ANDA status color)
}

const InfoCard: React.FC<InfoCardProps> = ({
  sectionType,
  title,
  date,
  description,
  tags,
  priority,
  url,
  onCardClick,
  cardColor,
}) => {
  const { handleCardClick } = useInfoCardLogic();

  // Render description with line breaks
  const renderDescription = () => {
    if (!description) return null;
    const lines = description.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Render title with line breaks for lossOfExclusivity section
  const renderTitle = () => {
    if (!title) return null;
    if (sectionType === 'lossOfExclusivity') {
      const lines = title.split('\n');
      return lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return title;
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCardClick) {
      onCardClick();
    } else {
      handleCardClick(title);
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`info-card priority-${priority}`}
      tabIndex={0}
      role="button"
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onCardClick) {
            onCardClick();
          } else {
            handleCardClick(title);
          }
        }
      }}
      style={cardColor ? { borderLeftColor: cardColor, borderLeftWidth: '4px' } : undefined}
    >
      <div className="card-header">
        <div className="card-title">{renderTitle()}</div>
        <div className="card-date">{date}</div>
      </div>
      {sectionType !== 'lossOfExclusivity' && (
        <div className="card-description">{renderDescription()}</div>
      )}
      <div className="card-tags">
        {tags &&
          tags.map((tag, index) => (
            <span key={index} className={`card-tag`}>
              {/* TODO: Add tag color based on tag type using the tag type ${tag.type} in th className */}
              {getTagText(tag)}
            </span>
          ))}
      </div>
      {url && (
        <button
          className="card-link-icon"
          onClick={handleLinkClick}
          aria-label="Open link in new tab"
          title="Open link in new tab"
        >
          <i className="pi pi-external-link"></i>
        </button>
      )}
    </div>
  );
};

export default InfoCard;
